import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

/**
 * Saved "Other Retailers": nearby retail a user has chosen to keep against one
 * of their assets.
 *
 * Linear: MYD-484.
 *
 * The scan results deliberately live in their own `scan` slot rather than in
 * `entities`. They are candidates, not records: most of them will never be
 * saved, they carry a different shape (`alreadySaved`, `savedId`), and mixing
 * them into the entity map would mean the map layer and the sidebar had to
 * filter saved from unsaved on every render.
 */

const serverMessage = (err: any, fallback: string): string =>
	err?.response?.data?.message || err?.message || fallback

const SLICE_NAME = 'otherRetailers'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

export type AnchorType = 'Location' | 'EvaluationSite'

export interface AnchorRef {
	anchorType: AnchorType
	anchorId: string
}

/**
 * One key per asset. Both the Locations map and the Projects map resolve to
 * the same Location, so they share a key and therefore share a list, which is
 * the intended behaviour: what is trading nearby is a property of the place.
 */
export const anchorKey = ({ anchorType, anchorId }: AnchorRef): string =>
	`${anchorType}:${anchorId}`

const emptyScan = {
	anchorKey: null as string | null,
	results: [] as any[],
	categories: [] as { key: string; label: string; count: number }[],
	radiusMiles: 5,
	namedChainsOnly: true,
	/** False means the provider did not answer, which reads differently from "none found". */
	providerOk: true,
	cached: false,
	scannedAt: null as string | null,
	isScanning: false,
	error: null as string | null,
}

const initialState = {
	byAnchor: {} as Record<string, string[]>,
	entities: {} as Record<string, any>,
	scan: { ...emptyScan },
	isLoading: false,
	isSaving: false,
	error: null as string | null,
}

export const fetchOtherRetailers = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (anchor: AnchorRef, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/other-retailers', {
				...prepConfig(getState),
				params: { anchorType: anchor.anchorType, anchorId: anchor.anchorId },
			})
			return { key: anchorKey(anchor), retailers: data }
		} catch (err) {
			return rejectWithValue(serverMessage(err, 'Failed to load nearby retailers'))
		}
	},
)

export const scanOtherRetailers = createAsyncThunk(
	`${SLICE_NAME}/scan`,
	async (
		args: AnchorRef & { radiusMiles?: number; categories?: string[]; namedChainsOnly?: boolean },
		{ getState, rejectWithValue },
	) => {
		try {
			const { data } = await axios.post('/api/other-retailers/scan', args, prepConfig(getState))
			return { key: anchorKey(args), ...data }
		} catch (err) {
			return rejectWithValue(serverMessage(err, 'Scan failed'))
		}
	},
)

export const saveOtherRetailers = createAsyncThunk(
	`${SLICE_NAME}/save`,
	async (args: AnchorRef & { items: any[] }, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/other-retailers', args, prepConfig(getState))
			return { key: anchorKey(args), retailers: data }
		} catch (err) {
			return rejectWithValue(serverMessage(err, 'Failed to save retailers'))
		}
	},
)

export const updateOtherRetailer = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (
		args: { id: string; category?: string; notes?: string },
		{ getState, rejectWithValue },
	) => {
		try {
			const { id, ...body } = args
			const { data } = await axios.put(`/api/other-retailers/${id}`, body, prepConfig(getState))
			return data
		} catch (err) {
			return rejectWithValue(serverMessage(err, 'Failed to update retailer'))
		}
	},
)

export const removeOtherRetailer = createAsyncThunk(
	`${SLICE_NAME}/remove`,
	async (args: AnchorRef & { id: string }, { getState, rejectWithValue }) => {
		try {
			await axios.delete(`/api/other-retailers/${args.id}`, prepConfig(getState))
			return { key: anchorKey(args), id: args.id }
		} catch (err) {
			return rejectWithValue(serverMessage(err, 'Failed to remove retailer'))
		}
	},
)

/** Replace one anchor's list wholesale. Both fetch and save return the full set. */
function absorbList(state: any, key: string, retailers: any[]) {
	const ids: string[] = []
	for (const retailer of retailers) {
		state.entities[retailer._id] = retailer
		ids.push(retailer._id)
	}
	state.byAnchor[key] = ids
}

const otherRetailerSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearScan: (state) => {
			state.scan = { ...emptyScan }
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchOtherRetailers.pending, (state) => {
				state.isLoading = true
				state.error = null
			})
			.addCase(fetchOtherRetailers.fulfilled, (state, action) => {
				absorbList(state, action.payload.key, action.payload.retailers)
				state.isLoading = false
			})
			.addCase(fetchOtherRetailers.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})

			.addCase(scanOtherRetailers.pending, (state) => {
				state.scan = { ...emptyScan, isScanning: true }
			})
			.addCase(scanOtherRetailers.fulfilled, (state, action) => {
				const p: any = action.payload
				state.scan = {
					anchorKey: p.key,
					results: p.results ?? [],
					categories: p.categories ?? [],
					radiusMiles: p.radiusMiles ?? 5,
					namedChainsOnly: Boolean(p.namedChainsOnly),
					providerOk: p.providerOk !== false,
					cached: Boolean(p.cached),
					scannedAt: p.scannedAt ?? null,
					isScanning: false,
					error: null,
				}
			})
			.addCase(scanOtherRetailers.rejected, (state, action) => {
				state.scan = { ...emptyScan, error: action.payload as string, providerOk: false }
			})

			.addCase(saveOtherRetailers.pending, (state) => {
				state.isSaving = true
				state.error = null
			})
			.addCase(saveOtherRetailers.fulfilled, (state, action) => {
				absorbList(state, action.payload.key, action.payload.retailers)
				state.isSaving = false
				// The scan is stale the moment anything is saved: every saved row
				// would still be offered as a candidate. Clearing it closes the
				// dialog's data behind it rather than showing a wrong list.
				state.scan = { ...emptyScan }
			})
			.addCase(saveOtherRetailers.rejected, (state, action) => {
				state.isSaving = false
				state.error = action.payload as string
			})

			.addCase(updateOtherRetailer.fulfilled, (state, action) => {
				const updated: any = action.payload
				// Merge rather than replace: the PUT response is the raw document
				// and does not carry the derived `logoUrl` / `monogram` fields the
				// list endpoint adds, so overwriting would blank the pin.
				state.entities[updated._id] = { ...state.entities[updated._id], ...updated }
			})
			.addCase(updateOtherRetailer.rejected, (state, action) => {
				state.error = action.payload as string
			})

			.addCase(removeOtherRetailer.fulfilled, (state, action) => {
				const { key, id } = action.payload
				delete state.entities[id]
				state.byAnchor[key] = (state.byAnchor[key] || []).filter((x: string) => x !== id)
			})
			.addCase(removeOtherRetailer.rejected, (state, action) => {
				state.error = action.payload as string
			})
	},
})

export const { clearScan } = otherRetailerSlice.actions
export default otherRetailerSlice
